import { ArrowChevronRightIcon } from '@tableau/icons/ArrowChevronRightIcon';
import { SignInUrlBuilder } from '@tableau/shared-urls/lib/src/SignInUrlBuilder';
import { ButtonType, ButtonWidget } from '@tableau/widgets';
import { useEffect } from 'react';
import React = require('react');
import { IIdentityPool } from '~src/authentication/serverApi/types/identityPools/IIdentityPool';
import { IdentityPoolService } from '~src/authentication/services/IdentityPoolService';
import { Messages } from '~src/codegen/Localize';
import { WindowLocationService } from '~src/commonInfra/services/WindowLocationService';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';

import { LoginErrorMessage } from '../../Login/LoginErrorMessage';
import { LoginPage } from '../../Login/LoginPage';
import { SigningInSpinner } from '../../loginSpace/SigningInSpinner';


export interface IdentityPoolsLoginPageProps {
  errorCode: number;
  signinQueryParams: SignInUrlBuilder.QueryParams;
}

const generalWidth = '318px';

export function IdentityPoolsLoginPage(props: IdentityPoolsLoginPageProps) {
  const [identityPools, setIdentityPools] = React.useState(null as IIdentityPool[]);
  const [defaultPoolDescription, setDefaultPoolDescription] = React.useState('');

  useEffect(() => {
    IdentityPoolService.tryFetchActiveIdentityPools().then((result: IIdentityPool[]) => {
      if (result.length === 0) {
        // No identity pools to show, only option is legacy login, choose it automatically.
        navigateToLegacyServerWideLoginPage();
      }
      setIdentityPools(result);
    });

    IdentityPoolService.getDefaultIdentityPoolDescription().then((result: IResult) => {
      if (!result.errors) {
        setDefaultPoolDescription(result as string);
      } else {
        // Fetch failed, nothing for us to do here, it was a best-effort approach.
      }
    });
  }, []);

  if (!identityPools) {
    // While the page loads we will show a spinner, to avoid users getting confused
    // about seeing IdPools and then automatically navigating away.
    return <LoginPage>
      <SigningInSpinner />
    </LoginPage>;
  }

  function renderTitle() {
    return <div style={{display: 'table', paddingTop: '28px', height: '48px', overflow: 'hidden', minWidth: generalWidth, margin: 'auto' }}>
      <div style={{display: 'table-cell', verticalAlign: 'middle'}}>
        <div style={{
            textAlign: 'center',
            fontWeight: 400,
            fontSize: 26,
        }}>
          { Messages.identityPools_LoginPage_SignInTitle() }
        </div>
      </div>
    </div>;
  }

  function renderDefaultPool() {
    return     <div>
    <ButtonWidget
      buttonType={ButtonType.Primary}
      style={{
        width: generalWidth,
        height: '48px',
        marginTop: '24px',
        marginBottom: '24px',
        fontSize: '16px',
        lineHeight: '24px',
      }}
      testId='default-identity-pool'
      onClick={navigateToLegacyServerWideLoginPage}>
      { Messages.signIn() }
    </ButtonWidget>

    { defaultPoolDescription && <div style={{
        width: generalWidth,
        height: '24px',
        fontSize: '14px',
        lineHeight: '24px',
        margin: 'auto',
      }}>
      { defaultPoolDescription }
    </div>}
  </div>;
  }

  function renderSeparator() {
    return <div className='row' style={{textAlign: 'center',  width: generalWidth, marginTop: '20px', marginBottom: '20px', marginLeft: 'auto', marginRight: 'auto'}}>
      <div className='column' style={{ width: '40%', float: 'left' }}>
        <hr />
      </div>
      <div className='column' style={{ width: '20%', float: 'left' }}>
        { Messages.identityPools_LoginPage_DefaultFromOtherPoolsSeparatorText() }
      </div>
      <div className='column' style={{ width: '40%', float: 'left' }}>
        <hr />
      </div>
    </div>;
  }

  function renderPool(identityPool: IIdentityPool) {
    return <ButtonWidget
    buttonType={ButtonType.Outline}
    testId={'identity-pool-' + identityPool.id}
    onClick={() => IdentityPoolService.kickoffLogin(identityPool, props.signinQueryParams)}
    style={{
          border: '1px solid',
          width: generalWidth,
          height: '100%',
          marginLeft: 'auto',
          marginRight: 'auto',
          marginTop: '8px',
          paddingLeft: '16px',
          paddingTop: '20px',
          paddingRight: '22px',
          paddingBottom: '16px'}}>

    <div style={{}}>
      <div className='row' style={{textAlign: 'center', overflow: 'hidden', paddingBottom: '4px'}}>
        <div className='column' style={{ float: 'left', fontSize: '16px', lineHeight: '24px', fontWeight: 500}}>
          { identityPool.name }
        </div>
        <div className='column' style={{ float: 'right', display: 'flex', alignItems: 'center', height: '24px' }}>
          <ArrowChevronRightIcon size={14} alt={Messages.signIn()} />
        </div>
      </div>

      { identityPool.description && <div style={{textAlign: 'left', whiteSpace: 'pre-wrap'}}>
        { identityPool.description }
      </div> }
    </div>
    </ButtonWidget>;
  }

  return <LoginPage>
    <div style={{width: '500px', display: 'flex', flexDirection: 'column'}}>

      {renderTitle()}

      <LoginErrorMessage errorCode={props.errorCode} />

      {renderDefaultPool()}

      {renderSeparator()}

      {identityPools.map((identityPool, index) => {
        return <div key={index}>
                     {renderPool(identityPool)}
        </div>;
      })}
    </div>
  </LoginPage>;
}

function navigateToLegacyServerWideLoginPage() {
  WindowLocationService.setHashQueryParameter(SignInUrlBuilder.QueryKey.isDefaultIdentityPoolLogin, 'true');
}
